import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { MaturityTrendPoint } from '../../graphql/types';
import './MaturityTrendChart.css';

export interface TrendSeries {
  id: string;
  label: string;
  color: string;
  points: MaturityTrendPoint[];
  scoreAccessor: (p: MaturityTrendPoint) => number;
}

export interface MaturityTrendChartProps {
  /** Array of trend series to render. */
  series: TrendSeries[];
  width?: number;
  height?: number;
}

const MARGIN = { top: 24, right: 32, bottom: 56, left: 48 };

function computeDelta(current: number, previous: number | undefined): string | null {
  if (previous === undefined) return null;
  const delta = current - previous;
  const sign = delta >= 0 ? '+' : '';
  const arrow = delta > 0 ? ' ↑' : delta < 0 ? ' ↓' : '';
  return `${sign}${delta.toFixed(1)}${arrow}`;
}

export function MaturityTrendChart({
  series,
  width = 640,
  height = 320,
}: MaturityTrendChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || series.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    // Collect all periods across all series, sorted
    const allPeriods = Array.from(
      new Set(series.flatMap((s) => s.points.map((p) => p.assessmentPeriod)))
    ).sort();

    if (allPeriods.length === 0) return;

    const xScale = d3.scalePoint<string>().domain(allPeriods).range([0, innerW]).padding(0.4);

    const allScores = series.flatMap((s) => s.points.map((p) => s.scoreAccessor(p)));
    const scoreMin = Math.max(1, Math.floor(d3.min(allScores) ?? 1) - 0.5);
    const scoreMax = Math.min(6, Math.ceil(d3.max(allScores) ?? 6) + 0.5);

    const yScale = d3.scaleLinear().domain([scoreMin, scoreMax]).range([innerH, 0]).nice();

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Grid lines
    const yTicks = yScale.ticks(5);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'trend-grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', 'var(--forge-theme-border, #e0e0e0)')
      .attr('stroke-dasharray', '3,3')
      .attr('stroke-width', 0.7);

    // X axis
    g.append('g')
      .attr('class', 'trend-x-axis')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', 'var(--forge-theme-text-medium, #757575)');

    // Y axis
    g.append('g')
      .attr('class', 'trend-y-axis')
      .call(d3.axisLeft(yScale).ticks(5))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', 'var(--forge-theme-text-medium, #757575)');

    // Y axis label
    g.append('text')
      .attr('transform', `rotate(-90)`)
      .attr('x', -innerH / 2)
      .attr('y', -36)
      .attr('dy', '1em')
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', 'var(--forge-theme-text-medium, #757575)')
      .text('Maturity Score (1–6)');

    // Line generator
    const lineGen = d3
      .line<{ period: string; score: number }>()
      .x((d) => xScale(d.period) ?? 0)
      .y((d) => yScale(d.score))
      .curve(d3.curveMonotoneX);

    // Draw each series
    series.forEach((s) => {
      const sorted = [...s.points].sort((a, b) =>
        a.assessmentPeriod.localeCompare(b.assessmentPeriod)
      );
      const lineData = sorted.map((p) => ({
        period: p.assessmentPeriod,
        score: s.scoreAccessor(p),
      }));

      if (lineData.length < 2) return;

      // Line path
      g.append('path')
        .datum(lineData)
        .attr('fill', 'none')
        .attr('stroke', s.color)
        .attr('stroke-width', 2)
        .attr('d', lineGen);

      // Data points + delta tooltips
      sorted.forEach((p, i) => {
        const score = s.scoreAccessor(p);
        const prev = i > 0 ? s.scoreAccessor(sorted[i - 1]) : undefined;
        const delta = computeDelta(score, prev);
        const cx = xScale(p.assessmentPeriod) ?? 0;
        const cy = yScale(score);

        const dotG = g.append('g').attr('class', 'trend-dot');

        dotG
          .append('circle')
          .attr('cx', cx)
          .attr('cy', cy)
          .attr('r', 5)
          .attr('fill', 'white')
          .attr('stroke', s.color)
          .attr('stroke-width', 2);

        dotG.append('circle').attr('cx', cx).attr('cy', cy).attr('r', 3).attr('fill', s.color);

        const tooltipLines = [
          `${s.label}`,
          `Period: ${p.assessmentPeriod}`,
          `Score: ${score.toFixed(1)}`,
        ];
        if (delta) tooltipLines.push(`Change: ${delta}`);
        dotG.append('title').text(tooltipLines.join('\n'));

        // Delta label above the dot (only from 2nd point onward)
        if (delta) {
          const isPositive = score > (prev ?? score);
          g.append('text')
            .attr('x', cx)
            .attr('y', cy - 12)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('fill', isPositive ? '#2ca02c' : '#d62728')
            .text(delta);
        }
      });
    });
  }, [series, width, height]);

  return (
    <div className="maturity-trend-chart">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        role="img"
        aria-label="Maturity score trend chart showing score progression over assessment periods"
      />
    </div>
  );
}
